/**
 * Service Requests repository — the ONLY place that touches `service_requests` data.
 *
 * Hooks (`useServiceRequests`) and components call these functions; nothing else
 * may call `supabase.from("service_requests")` directly. Reference template for
 * the Strangler-Fig per-slice migration (manifest-6):
 *   Step 1 (done)  — consolidated scattered supabase calls here.
 *   Step 2 (done)  — guarded NestJS endpoints (`apps/api/src/service-requests/*`).
 *   Step 3 (HERE)  — staff + super-admin reads/writes flipped to the Nest client;
 *                    only the owner-portal reads/writes stay on Supabase (see the
 *                    authz split below).
 *
 * ── Flip scope ───────────────────────────────────────────────────────────────
 *   FLIPPED to Nest (with a Supabase fallback): `list`, `getById`, `update`,
 *     `stats` — ALL roles. The guarded endpoints resolve scope from the JWT, so
 *     super_admin reads/writes cross-company server-side (the controller skips the
 *     company filter only for super_admin); the FE no longer gates on scopeToCompany.
 *   KEPT on Supabase (documented): `create` — owner-portal callers
 *     lack `service_requests.create`, so the guarded endpoint would 403 them.
 *   Requester-name enrichment stays client-side (the Nest list doesn't enrich).
 *
 * Each flipped method tries Nest first (only when an API is configured) and falls
 * back to the original Supabase query on any error — so this is a reversible unit
 * and local dev without the Nest API keeps working. RLS is untouched.
 */
import {
  isDefinitiveNotFound,
  reportNestFallback,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";
import { CardStatus, NoteStatus } from "@/lib/serviceRequestEnums";
import { z } from "zod";
import { serviceRequestSchema, type ServiceRequest } from "@dallali/shared";
import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";
import { companiesRepository } from "@/repositories/companies";
import { nafathVerificationsRepository } from "@/repositories/nafathVerifications";

export type { ServiceRequest };

const TABLE = "service_requests";

/**
 * Status-count shape the dashboard cards render — also the exact JSON the guarded
 * `GET /service-requests/stats` returns. Parsing the Nest response against this is
 * the no-silent-fallback gate (a drifted/garbage payload throws → Supabase fallback).
 */
const serviceRequestStatsSchema = z.object({
  total: z.number(),
  new: z.number(),
  under_review: z.number(),
  under_inspection: z.number(),
  approved: z.number(),
  rejected: z.number(),
});
export type ServiceRequestStats = z.infer<typeof serviceRequestStatsSchema>;

/**
 * One entry of the super_admin dashboard's company tab strip — also the exact
 * JSON the guarded `GET /service-requests/companies` returns. `is_main` lets the
 * UI pin the platform main company first and select it by default.
 */
const serviceRequestCompanySchema = z.object({
  company_id: z.string(),
  name: z.string(),
  name_en: z.string().nullable(),
  total: z.number(),
  is_main: z.boolean(),
});
export type ServiceRequestCompany = z.infer<typeof serviceRequestCompanySchema>;

/**
 * Master Card board row — the exact JSON `GET /service-requests/master-cards`
 * returns. `card_status` is validated as a string here and narrowed to CardStatus
 * in the mapper (mirrors the Supabase fallback's cast, tolerant of an unknown
 * value). Parsing the Nest payload against this is the no-silent-fallback gate. (S1)
 */
const masterCardListRowSchema = z.object({
  id: z.string(),
  request_number: z.string().nullable(),
  service_type: z.string(),
  card_status: z.string(),
  unit_count: z.number(),
  created_at: z.string(),
  // F4 · richer details
  property_name: z.string().nullable(),
  property_type: z.string().nullable(),
  requester_name: z.string().nullable(),
  // F6 · card completion 0–100
  completion_pct: z.number(),
});

// ── Nested Master Card detail schema (GET /:id/detail) — the no-silent-fallback
//    gate for the view read. Enum-ish fields stay z.string() (structural drift
//    triggers the fallback; a new-but-valid status value must NOT), and the parsed
//    result is asserted to MasterCardDetail at the call site (the existing idiom). (S1)
const mcStepSchema = z.object({
  id: z.string(),
  step_key: z.string(),
  step_status: z.string(),
  step_order: z.number(),
  is_conditional: z.boolean(),
  assigned_employee_id: z.string().nullable(),
  document_url: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
});
const mcActionSchema = z.object({
  id: z.string(),
  action_type: z.string(),
  status: z.string(),
  amount: z.number().nullable(),
  vat_amount: z.number().nullable(),
  quote_document_url: z.string().nullable(),
  admin_review_status: z.string().nullable(),
  owner_decision: z.string().nullable(),
  reject_reason: z.string().nullable(),
  completion_report_url: z.string().nullable(),
  invoice_id: z.string().nullable(),
  assigned_provider_id: z.string().nullable(),
  published: z.boolean(),
  created_at: z.string(),
});
const mcUnitSchema = z.object({
  id: z.string(),
  company_id: z.string(),
  unit_id: z.string(),
  unit_number: z.string().nullable(),
  unit_type: z.string().nullable(),
  unit_status: z.string(),
  // Unit specs for the task-modal per-unit detail (B10).
  bedrooms: z.number().nullable().default(null),
  bathrooms: z.number().nullable().default(null),
  area: z.union([z.number(), z.string()]).nullable().default(null),
  ready_for_marketing: z.boolean(),
  needs_maintenance: z.boolean(),
  needs_cleaning: z.boolean(),
  current_step_key: z.string().nullable(),
  created_at: z.string(),
  // Per-unit service-fee invoice (owner pays this to request the service).
  service_fee_invoice_id: z.string().nullable().default(null),
  service_fee_amount: z.union([z.number(), z.string()]).nullable().default(null),
  service_fee_balance: z.union([z.number(), z.string()]).nullable().default(null),
  service_fee_status: z.string().nullable().default(null),
  // Contracting: the owner-entered per-unit rent + deposit (contract terms).
  rent_amount: z.union([z.number(), z.string()]).nullable().default(null),
  deposit_amount: z.union([z.number(), z.string()]).nullable().default(null),
  steps: z.array(mcStepSchema),
  actions: z.array(mcActionSchema),
});
const mcActivitySchema = z.object({
  id: z.string(),
  unit_id: z.string().nullable(),
  actor_id: z.string().nullable(),
  actor_role: z.string().nullable(),
  action: z.string(),
  reason: z.string().nullable(),
  status: z.string().nullable(),
  assigned_party_id: z.string().nullable(),
  created_at: z.string(),
});
const mcNoteSchema = z.object({
  id: z.string(),
  service_request_unit_id: z.string().nullable(),
  note: z.string(),
  photo_url: z.string().nullable(),
  status: z.string(),
  converted_action_id: z.string().nullable(),
  reported_by: z.string().nullable(),
  created_at: z.string(),
});
const masterCardDetailSchema = z.object({
  id: z.string(),
  company_id: z.string(),
  owner_id: z.string().nullable(),
  property_id: z.string().nullable(),
  service_type: z.string(),
  status: z.string().nullable(),
  card_status: z.string(),
  total_fees: z.coerce.number().nullable(),
  vat_amount: z.coerce.number().nullable(),
  payment_status: z.string().nullable(),
  request_number: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  units: z.array(mcUnitSchema),
  activity: z.array(mcActivitySchema),
  notes: z.array(mcNoteSchema),
  contract_intake: z.record(z.string()).nullable(),
  // F5 · admin can return the card to the owner with a reason
  return_reason: z.string().nullable(),
  returned_at: z.string().nullable(),
  // Property identity for the card header (the owner identifies by property).
  property_name: z.string().nullable().default(null),
  property_type: z.string().nullable().default(null),
});

/** Task-board per-card context map (GET /master-cards/task-context). */
const taskContextSchema = z.record(
  z.object({
    requestNumber: z.string().nullable(),
    total: z.number(),
    order: z.record(z.number()),
  }),
);
/** Inspection/visit-step rows (GET /tasks/:taskId/{inspection,visit}-steps). */
const inspectionStepSchema = z.object({
  id: z.string(),
  unitId: z.string(),
  // The unit's real number/name (so the employee sees it, not "Unit 1/2").
  unitNumber: z.string().nullable().default(null),
  // The employee's own submission — shown back to them after submitting (not just
  // a "submitted" confirmation). Photos are signed URLs.
  notes: z.string().nullable().default(null),
  photos: z.array(z.string()).default([]),
  submitted: z.boolean(),
  // Admin returned the report → show the reason so the employee can re-do it.
  returned: z.boolean().default(false),
  reviewNotes: z.string().nullable().default(null),
  // Inspection steps carry the card's service_type so the FE can render a
  // report-only assessment for 'contracting' (no maintenance/cleaning). Absent
  // on visit-steps, which reuse this schema — hence optional.
  serviceType: z.string().optional(),
});
/** One version of the employee's uploaded contract-draft PDF (+ notes). */
const contractDraftVersionSchema = z.object({
  version: z.number(),
  pdfUrl: z.string().nullable().default(null),
  employeeNote: z.string().nullable().default(null),
  ownerNote: z.string().nullable().default(null),
  status: z.string(),
  submittedAt: z.string().nullable().default(null),
  reviewedAt: z.string().nullable().default(null),
});
export type ContractDraftVersion = z.infer<typeof contractDraftVersionSchema>;
/** Contract-step rows (GET /tasks/:taskId/contract-steps) — carry the intake. */
const contractStepSchema = inspectionStepSchema.extend({
  contractIntake: z.record(z.string()).nullable().default(null),
  // The real property + unit the contract wizard is pre-filled with on activate.
  propertyId: z.string().nullable().default(null),
  propertyUnitId: z.string().nullable().default(null),
  // Owner-entered per-unit rent + deposit (so the drafter sees the terms).
  rentAmount: z.number().nullable().default(null),
  depositAmount: z.number().nullable().default(null),
  // The card's service. For 'contracting' the draft is aggregate (one contract
  // for ALL units); every other service keeps a per-unit entry.
  serviceType: z.string().default(""),
  // The property context the employee needs to draft the contract (deed,
  // purpose, type, location + map). Optional so older Nest payloads still parse.
  property: z
    .object({
      deedNumber: z.string().nullable().default(null),
      purpose: z.string().nullable().default(null),
      propertyType: z.string().nullable().default(null),
      address: z.string().nullable().default(null),
      city: z.string().nullable().default(null),
      district: z.string().nullable().default(null),
      latitude: z.number().nullable().default(null),
      longitude: z.number().nullable().default(null),
    })
    .nullable()
    .optional(),
  // Every unit this one draft covers (its real number + rent/deposit + specs).
  // One entry for per-unit services; all the card's units for contracting.
  units: z
    .array(
      z.object({
        unitId: z.string(),
        unitNumber: z.string().nullable().default(null),
        rentAmount: z.number().nullable().default(null),
        depositAmount: z.number().nullable().default(null),
        // Per-unit specs for the drafter.
        unitType: z.string().nullable().default(null),
        bedrooms: z.number().nullable().default(null),
        bathrooms: z.number().nullable().default(null),
        // The Nest wire casts area::float8, so only number | null can arrive.
        area: z.number().nullable().default(null),
        floorNumber: z.number().nullable().default(null),
      }),
    )
    .default([]),
  // Owner accepted the latest draft.
  approved: z.boolean().default(false),
  // A live contract has been created (unit's work is done).
  activated: z.boolean().default(false),
  // The full PDF-version history (newest first), each with employee + owner note.
  versions: z.array(contractDraftVersionSchema).default([]),
});
export type ContractStep = z.infer<typeof contractStepSchema>;
/** Task completion rows (GET /tasks/:taskId/action-completions). */
const taskActionCompletionSchema = z.object({
  action_id: z.string(),
  unit_id: z.string(),
  action_type: z.string(),
  status: z.string(),
  unit_number: z.string().nullable(),
});
export type TaskActionCompletion = z.infer<typeof taskActionCompletionSchema>;
/** Assign-picker rows (GET /companies/:id/assignable-{employees,providers}). */
const assignableEmployeeSchema = z.object({ id: z.string(), full_name: z.string().nullable() });
const assignableProviderSchema = z.object({ id: z.string(), name: z.string().nullable() });
/** F1 · a per-unit document with a fresh signed read URL. */
const unitDocumentSchema = z.object({
  id: z.string(),
  doc_type: z.string(),
  caption: z.string().nullable(),
  uploaded_role: z.string().nullable(),
  created_at: z.string(),
  url: z.string().nullable(),
});
export type UnitDocument = z.infer<typeof unitDocumentSchema>;
/** F1 · a card-wide document row (admin aggregate) — labelled by its unit. */
const cardDocumentSchema = z.object({
  id: z.string(),
  service_request_unit_id: z.string(),
  doc_type: z.string(),
  caption: z.string().nullable(),
  created_at: z.string(),
  url: z.string().nullable(),
});
export type CardDocument = z.infer<typeof cardDocumentSchema>;
/** F3 · the composer payload shown with a quote (note + assessment flag + signed attachments). */
const actionQuoteDetailsSchema = z.object({
  quote_note: z.string().nullable(),
  include_assessment: z.boolean(),
  // Cleaning-after-maintenance: include the maintenance completion note instead
  // of the assessment. `maintenance_note` is the sibling maintenance action's
  // completion note (null when no maintenance preceded).
  include_maintenance_report: z.boolean().default(false),
  maintenance_note: z.string().nullable().default(null),
  completion_note: z.string().nullable(),
  attachments: z.array(
    z.object({
      id: z.string(),
      caption: z.string().nullable(),
      doc_type: z.string(),
      url: z.string().nullable(),
    }),
  ),
  // F3 · the unit's assessment, resolved only when include_assessment is on.
  assessment: z
    .object({
      ready_for_marketing: z.boolean(),
      needs_maintenance: z.boolean(),
      needs_cleaning: z.boolean(),
      notes: z.string().nullable(),
      photos: z.array(z.string()),
    })
    .nullable(),
});
export type ActionQuoteDetails = z.infer<typeof actionQuoteDetailsSchema>;

// ── Master Card detail (MC-B4/B8) — shape of public.v_service_request_detail ──
export type UnitStatus = "pending" | "in_progress" | "paused" | "completed";
export type { CardStatus };
export type StepStatus = "pending" | "active" | "completed" | "skipped";

export interface MasterCardStep {
  id: string;
  step_key: string;
  step_status: StepStatus;
  step_order: number;
  is_conditional: boolean;
  assigned_employee_id: string | null;
  document_url: string | null;
  metadata: Record<string, unknown> | null;
  started_at: string | null;
  completed_at: string | null;
}
// ── Actions (MC-E maintenance / MC-F cleaning) ──
export type ActionType = "maintenance" | "cleaning";
export type ActionStatus =
  | "pending" | "quoted" | "owner_decision" | "negotiation"
  | "awaiting_payment"
  | "in_progress" | "completed_pending_admin" | "completed" | "cancelled";
export interface MasterCardAction {
  id: string;
  action_type: ActionType;
  status: ActionStatus;
  amount: number | null;
  vat_amount: number | null;
  quote_document_url: string | null;
  admin_review_status: string | null;
  owner_decision: string | null;
  reject_reason: string | null;
  completion_report_url: string | null;
  assigned_provider_id: string | null;
  /** Invoice raised when the owner approved the quote (Pkg 5) — drives Pay Now. */
  invoice_id: string | null;
  published: boolean;
  created_at: string;
}
export interface MasterCardUnit {
  id: string;
  company_id: string;
  unit_id: string;
  unit_number: string | null;
  unit_type: string | null;
  unit_status: UnitStatus;
  bedrooms: number | null;
  bathrooms: number | null;
  area: number | string | null;
  ready_for_marketing: boolean;
  needs_maintenance: boolean;
  needs_cleaning: boolean;
  current_step_key: string | null;
  created_at: string;
  /** Per-unit service-fee invoice (owner pays this to request the service). */
  service_fee_invoice_id: string | null;
  service_fee_amount: number | string | null;
  service_fee_balance: number | string | null;
  service_fee_status: string | null;
  /** Contracting: owner-entered per-unit rent + deposit (contract terms). */
  rent_amount: number | string | null;
  deposit_amount: number | string | null;
  steps: MasterCardStep[];
  actions: MasterCardAction[];
}
// ── Provider portal (MC-I) — task-scoped view of a provider's own action ──
export interface ProviderAction {
  id: string;
  action_type: ActionType;
  status: ActionStatus;
  amount: number | null;
  vat_amount: number | null;
  quote_document_url: string | null;
  completion_report_url: string | null;
  unit_number: string | null;
  property_name: string | null;
  service_type: string;
  request_number: string | null;
  created_at: string;
}

// ── Ad-hoc notes (MC-H) ──
export interface MasterCardNote {
  id: string;
  service_request_unit_id: string | null;
  note: string;
  photo_url: string | null;
  status: NoteStatus;
  converted_action_id: string | null;
  reported_by: string | null;
  created_at: string;
}
export interface MasterCardActivity {
  id: string;
  unit_id: string | null;
  actor_id: string | null;
  actor_role: string | null;
  action: string;
  reason: string | null;
  status: string | null;
  assigned_party_id: string | null;
  created_at: string;
}
export interface MasterCardDetail {
  id: string;
  company_id: string;
  owner_id: string | null;
  property_id: string | null;
  service_type: string;
  /** Request-level intake (contract or marketing) the owner filled before pay,
   *  or null. A flat string map — see ContractIntake / MarketingIntake. */
  contract_intake: Record<string, string> | null;
  status: string | null;
  card_status: CardStatus;
  total_fees: number | null;
  vat_amount: number | null;
  payment_status: string | null;
  request_number: string | null;
  created_at: string;
  updated_at: string;
  units: MasterCardUnit[];
  activity: MasterCardActivity[];
  notes: MasterCardNote[];
  /** F5 · reason the admin returned the card to the owner (null = not returned). */
  return_reason: string | null;
  returned_at: string | null;
  /** Property identity for the card header (owner identifies by property). */
  property_name: string | null;
  property_type: string | null;
}

// ── Intake (MC-A5/A6) ────────────────────────────────────────────────────────
export interface AvailableUnit {
  unit_id: string;
  unit_number: string | null;
  is_available: boolean;
  unavailable_reason: "under_contract" | "active_request" | null;
}
export interface CreateCardParams {
  ownerId: string;
  propertyId: string;
  serviceType: string;
  unitIds: string[];
  idempotencyKey?: string;
  /**
   * Owner-supplied contract details from the إبرام العقود intake form. Parked
   * on the request as jsonb — it is not a contract until ops issue one.
   */
  contractIntake?: Record<string, string>;
  /** Contracting only: per-unit rent + deposit for each selected unit. */
  unitAmounts?: { unit_id: string; rent_amount: string; deposit_amount: string }[];
}
export interface CreateCardResult {
  service_request_id: string;
  request_number: string | null;
  total_fees?: number;
  vat_amount?: number;
  unit_count?: number;
  idempotent: boolean;
}

interface ScopeOptions {
  companyId: string | null | undefined;
  /** When false, skip the company_id filter (super-admin cross-company access). */
  scopeToCompany?: boolean;
  /**
   * super_admin only: scope the cross-company read to a single company — the
   * dashboard's company tab. When set, the Nest path appends `?companyId=` and the
   * Supabase fallback filters on it. Ignored for company-scoped roles (they are
   * already pinned to their own company via `scopeToCompany`).
   */
  selectedCompanyId?: string | null;
  /** Optional created_at bounds (ISO) — statsByStatus/stats only. Unused by list(). */
  startDate?: string;
  endDate?: string;
}

// Authenticated Nest request bound to the "service_requests" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
const nestRequest = makeNestRequest("service_requests");

/**
 * Enrich requests with requester names from `nafath_verifications` (batched
 * via nafathVerificationsRepository.listCompletedNamesByNationalIds — the
 * same lookup the unified-tasks board uses). Mutates the passed rows in
 * place, mirroring the original hook behavior.
 */
async function enrichWithRequesterNames(requests: ServiceRequest[]): Promise<void> {
  const uniqueNationalIds = [...new Set(requests.map((r) => r.national_id).filter(Boolean))];
  if (uniqueNationalIds.length === 0) return;

  const nafathRecords =
    await nafathVerificationsRepository.listCompletedNamesByNationalIds(
      uniqueNationalIds,
    );

  const nameMap = new Map<string, string>();
  for (const rec of nafathRecords) {
    if (!nameMap.has(rec.national_id)) {
      const name = [rec.arabic_first_name, rec.arabic_family_name].filter(Boolean).join(" ");
      if (name) nameMap.set(rec.national_id, name);
    }
  }
  for (const req of requests) {
    req.requester_name = nameMap.get(req.national_id) || null;
  }
}

// ── Supabase implementations (fallback for the flipped methods, and the live
//    path for the owner/super-admin/stats methods kept on Supabase) ───────────

async function listViaSupabase(
  { companyId, scopeToCompany = true, selectedCompanyId }: ScopeOptions,
): Promise<ServiceRequest[]> {
  let query = supabase.from(TABLE).select("*");
  // Company-scoped roles: pin to their own company. super_admin (scopeToCompany:
  // false): cross-company, OR scoped to a single company when the dashboard tab
  // selects one (selectedCompanyId) — so the list count and the cards agree.
  if (scopeToCompany) query = query.eq("company_id", companyId as string);
  else if (selectedCompanyId) query = query.eq("company_id", selectedCompanyId);

  const { data, error } = await query.order("created_at", { ascending: false });
  if (error) throw error;
  return data as ServiceRequest[];
}

/**
 * Supabase fallback for the company tab strip. Mirrors the Nest repository:
 * count requests per company, ensure the main company is always present for
 * super_admin (via get_main_company_id), join names, order main-first then by
 * total. Reads `companies` here (a one-off cross-domain read for the fallback);
 * the live path is the guarded Nest endpoint above.
 */
async function companiesWithRequestsViaSupabase(
  { companyId, scopeToCompany = true }: ScopeOptions,
): Promise<ServiceRequestCompany[]> {
  let countQuery = supabase.from(TABLE).select("company_id");
  if (scopeToCompany) countQuery = countQuery.eq("company_id", companyId as string);
  const { data: rows, error } = await countQuery;
  if (error) throw error;

  const totals = new Map<string, number>();
  for (const r of (rows ?? []) as { company_id: string | null }[]) {
    if (!r.company_id) continue; // orphan rows (null company) can't be a tab
    totals.set(r.company_id, (totals.get(r.company_id) ?? 0) + 1);
  }

  let mainCompanyId: string | null = null;
  if (!scopeToCompany) {
    // Route through the companies repository chokepoint (slice a14 / N-3) — no
    // inline supabase.rpc("get_main_company_id"). Best-effort: a failed main
    // lookup just omits the main-company tab; it never breaks the stats read.
    try {
      mainCompanyId = await companiesRepository.getMainCompanyId();
    } catch {
      mainCompanyId = null;
    }
    if (mainCompanyId && !totals.has(mainCompanyId)) totals.set(mainCompanyId, 0);
  }

  const ids = [...totals.keys()];
  if (ids.length === 0) return [];

  const { data: companies } = await supabase
    .from("companies")
    .select("id, name, name_en")
    .in("id", ids);
  const nameMap = new Map(
    ((companies ?? []) as { id: string; name: string; name_en: string | null }[]).map(
      (c) => [c.id, c],
    ),
  );

  const result: ServiceRequestCompany[] = ids.map((id) => {
    const c = nameMap.get(id);
    return {
      company_id: id,
      name: c?.name ?? "",
      name_en: c?.name_en ?? null,
      total: totals.get(id) ?? 0,
      is_main: id === mainCompanyId,
    };
  });
  result.sort(
    (a, b) => (b.is_main ? 1 : 0) - (a.is_main ? 1 : 0) || b.total - a.total,
  );
  return result;
}

async function getByIdViaSupabase(
  id: string,
  { companyId, scopeToCompany = true }: ScopeOptions,
): Promise<ServiceRequest | null> {
  let query = supabase.from(TABLE).select("*").eq("id", id);
  if (scopeToCompany) query = query.eq("company_id", companyId as string);

  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  return data as ServiceRequest | null;
}

async function updateViaSupabase(
  id: string,
  patch: TablesUpdate<"service_requests">,
  { companyId, scopeToCompany = true, returning = false }: ScopeOptions & { returning?: boolean },
): Promise<ServiceRequest | null> {
  let query = supabase.from(TABLE).update(patch).eq("id", id);
  if (scopeToCompany) query = query.eq("company_id", companyId as string);

  if (!returning) {
    const { error } = await query;
    if (error) throw error;
    return null;
  }

  const { data, error } = await query.select().maybeSingle();
  if (error) throw error;
  return data as ServiceRequest | null;
}

export const serviceRequestsRepository = {
  /**
   * Request list, newest first, with requester-name enrichment.
   * Staff op — flipped to Nest (scope resolved from the JWT: super_admin reads
   * cross-company there). NOTE: the Nest path ignores `companyId`/`scopeToCompany`
   * (the guarded endpoint derives scope from the token); those options only apply
   * to the Supabase fallback below, which honours `scopeToCompany` so the
   * super_admin fallback stays cross-company and the list count matches `stats`
   * (both endpoints agree for super_admin — no own-company subset).
   */
  async list({ companyId, scopeToCompany = true, selectedCompanyId }: ScopeOptions): Promise<ServiceRequest[]> {
    if (API_URL) {
      try {
        // selectedCompanyId only narrows the super_admin cross-company read; the
        // guarded endpoint ignores it for company-scoped roles.
        const rows = await nestRequest<unknown[]>(
          API_ROUTES.serviceRequests.list(selectedCompanyId ?? undefined),
        );
        const requests = z.array(serviceRequestSchema).parse(rows);
        await enrichWithRequesterNames(requests);
        return requests;
      } catch (err) {
        reportNestFallback("serviceRequests.list", err);
      }
    }
    const requests = await listViaSupabase({ companyId, scopeToCompany, selectedCompanyId });
    await enrichWithRequesterNames(requests);
    return requests;
  },

  /**
   * Single request by id; company-scoped unless scopeToCompany is false.
   * Flipped to Nest for ALL roles — the guarded endpoint resolves scope from the
   * JWT (super_admin reads cross-company there). Supabase remains the fallback and
   * the local-dev path; the fallback still honours scopeToCompany.
   */
  async getById(id: string, opts: ScopeOptions): Promise<ServiceRequest | null> {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.serviceRequests.getById(id));
        return row ? serviceRequestSchema.parse(row) : null;
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
        reportNestFallback("serviceRequests.getById", err);
      }
    }
    return getByIdViaSupabase(id, opts);
  },

  /**
   * Requests for a given requester phone within a company (owner portal).
   * Nest-first via the guarded GET /service-requests/owner-requests; the
   * Supabase fallback (local dev / Nest error) is retained for parity with the
   * other read methods. After the owner SELECT RLS bridge was retired
   * (20260610230000) the fallback SELECT returns an empty set rather than the
   * owner's rows — an acceptable degradation on the rare fallback path (the
   * live path is Nest, which is the sole reader).
   */
  async listByPhone(phone: string, companyId: string): Promise<ServiceRequest[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(
          API_ROUTES.serviceRequests.ownerRequests(),
        );
        return z.array(serviceRequestSchema).parse(rows);
      } catch (err) {
        reportNestFallback("serviceRequests.listByPhone", err);
      }
    }
    const { data, error } = await supabase
      .from(TABLE)
      .select("*")
      .eq("phone", phone)
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as ServiceRequest[];
  },

  /**
   * Insert a new owner service request via the guarded Nest endpoint; returns
   * the generated request_number. Nest-only: the auth-only POST
   * /service-requests/owner-requests resolves the owner from the JWT,
   * service-role inserts (bypasses RLS), and is the sole writer.
   *
   * The Supabase fallback was retired with the owner SELECT RLS bridge
   * (20260610230000): the direct-Supabase insert used `.select()`
   * (INSERT ... RETURNING), whose RETURNING needed the bridge SELECT policy.
   * With the create on Nest, neither the fallback nor the bridge is needed.
   * `nestRequest` throws a clear NestRequestError when VITE_API_URL is unset.
   */
  async create(payload: TablesInsert<"service_requests">): Promise<{ request_number: string | null } | null> {
    // company_id is derived server-side from the JWT; CreateServiceRequestDto
    // has none and the global forbidNonWhitelisted ValidationPipe 400s an
    // unknown property, so strip it from the body.
    const { company_id: _companyId, ...nestBody } = payload;
    return nestRequest<{ request_number: string | null }>(
      API_ROUTES.serviceRequests.ownerCreate(),
      { method: "POST", body: JSON.stringify(nestBody) },
    );
  },

  /**
   * One Master Card (MC-B4/B8) with its units — each carrying ordered steps —
   * and its activity log, from the security_invoker view v_service_request_detail.
   * RLS enforces company + owner scoping on the view exactly as on the base
   * tables; `companyId` adds a belt-and-braces app-level filter (pass `undefined`
   * for super_admin, whose cross-company reads intentionally skip it — mirrors
   * `getById`/`listMasterCards`). Kept on Supabase: it's the reusable read model
   * called out for BOTH the admin and owner card views (a guarded Nest endpoint
   * is the long-term Strangler home). The view isn't in the generated types, so
   * the relation name is cast (tenants_with_status idiom) and the row shape
   * re-asserted.
   */
  async getCardDetail(id: string, companyId?: string | null): Promise<MasterCardDetail | null> {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.serviceRequests.cardDetail(id));
        return row ? (masterCardDetailSchema.parse(row) as unknown as MasterCardDetail) : null;
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
        reportNestFallback("serviceRequests.getCardDetail", err);
      }
    }
    // ── Supabase fallback (local dev / Nest error) ──────────────────────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let query = supabase.from("v_service_request_detail" as any).select("*").eq("id", id);
    if (companyId) query = query.eq("company_id", companyId);

    const { data, error } = await query.maybeSingle();
    if (error) throw error;
    return (data ?? null) as unknown as MasterCardDetail | null;
  },

  /**
   * Owner-portal variant of {@link getCardDetail} — the authenticated owner's OWN
   * card via the guarded GET /service-requests/owner-requests/:id/detail (scoped to
   * the caller's owner ids server-side). Nest-only: after the owner SELECT RLS
   * bridge retired, the Supabase fallback returns null (mirrors `listByPhone`). (S1)
   */
  async getOwnerCardDetail(id: string): Promise<MasterCardDetail | null> {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.serviceRequests.ownerCardDetail(id));
        return row ? (masterCardDetailSchema.parse(row) as unknown as MasterCardDetail) : null;
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null;
        reportNestFallback("serviceRequests.getOwnerCardDetail", err);
      }
    }
    return null;
  },

  /**
   * MC-B4 · list all Master Cards (service requests that have per-unit rows) for
   * the admin board. RLS on service_requests already enforces company scoping;
   * `companyId` adds a belt-and-braces app-level filter (pass `undefined` for
   * super_admin, whose cross-company reads intentionally skip it). Returns the
   * header + unit count so the list can link into the per-unit card view.
   */
  async listMasterCards(companyId?: string | null): Promise<
    Array<{ id: string; request_number: string | null; service_type: string; card_status: CardStatus; unit_count: number; created_at: string; property_name: string | null; property_type: string | null; requester_name: string | null; completion_pct: number }>
  > {
    if (API_URL) {
      try {
        const rows = z
          .array(masterCardListRowSchema)
          .parse(
            await nestRequest<unknown[]>(
              API_ROUTES.serviceRequests.masterCards(companyId ?? undefined),
            ),
          );
        return rows.map((r) => ({
          id: r.id,
          request_number: r.request_number,
          service_type: r.service_type,
          card_status: (r.card_status as CardStatus) ?? CardStatus.New,
          unit_count: r.unit_count,
          created_at: r.created_at,
          property_name: r.property_name,
          property_type: r.property_type,
          requester_name: r.requester_name,
          completion_pct: r.completion_pct,
        }));
      } catch (err) {
        reportNestFallback("serviceRequests.listMasterCards", err);
      }
    }
    // ── Supabase fallback (local dev / Nest error) ──────────────────────────
    // Only cards with units are Master Cards (inner join filters out legacy rows).
    let query = supabase
      .from("service_requests")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .select("id, request_number, service_type, card_status, created_at, units:service_request_units!inner(count)" as any);
    if (companyId) query = query.eq("company_id", companyId);
    const { data, error } = await query.order("created_at", { ascending: false });
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data ?? []) as any[])
      .map((r) => ({
        id: r.id as string,
        request_number: (r.request_number as string | null) ?? null,
        service_type: r.service_type as string,
        card_status: (r.card_status as CardStatus) ?? CardStatus.New,
        unit_count: Array.isArray(r.units) ? Number(r.units[0]?.count ?? 0) : 0,
        created_at: r.created_at as string,
        // F4/F6 fields aren't joined in the rare Supabase fallback — degrade.
        property_name: null,
        property_type: null,
        requester_name: null,
        completion_pct: 0,
      }))
      // PostgREST's `!inner` with a count-only embed still returns 0-unit parents
      // (the aggregate is computed over the empty set), so legacy Add-Property
      // rows (0 units) leak in — filter them out: a Master Card always has units.
      .filter((r) => r.unit_count > 0);
  },

  /**
   * Board context for Master Card pipeline tasks: per service_request, the
   * request number, the total step count (pipeline length) and each step_key's
   * order. Lets the task board render "Master Card RER-xxx · Step 4 of 8" so a
   * per-step task reads as one continuous journey, not a disconnected ticket.
   */
  async getMasterCardTaskContext(
    serviceRequestIds: string[],
  ): Promise<Record<string, { requestNumber: string | null; total: number; order: Record<string, number> }>> {
    const ids = [...new Set(serviceRequestIds)].filter(Boolean);
    if (ids.length === 0) return {};
    if (API_URL) {
      try {
        return taskContextSchema.parse(
          await nestRequest<unknown>(
            API_ROUTES.serviceRequests.masterCardTaskContext(ids),
          ),
        ) as Record<string, { requestNumber: string | null; total: number; order: Record<string, number> }>;
      } catch (err) {
        reportNestFallback("serviceRequests.getMasterCardTaskContext", err);
      }
    }
    // ── Supabase fallback (local dev / Nest error) ──────────────────────────
    const [reqRes, stepRes] = await Promise.all([
      supabase.from(TABLE).select("id, request_number").in("id", ids),
      // service_request_steps isn't in the generated Supabase types yet — cast.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any)
        .from("service_request_steps")
        .select("step_key, step_order, service_request_units!inner(service_request_id)")
        .in("service_request_units.service_request_id", ids),
    ]);
    if (stepRes.error) throw stepRes.error;
    const map: Record<string, { requestNumber: string | null; total: number; order: Record<string, number> }> = {};
    for (const r of (reqRes.data ?? []) as Array<{ id: string; request_number: string | null }>) {
      map[r.id] = { requestNumber: r.request_number, total: 0, order: {} };
    }
    for (const s of ((stepRes.data ?? []) as Array<{
      step_key: string;
      step_order: number;
      service_request_units: { service_request_id: string } | { service_request_id: string }[] | null;
    }>)) {
      const rel = Array.isArray(s.service_request_units) ? s.service_request_units[0] : s.service_request_units;
      const srId = rel?.service_request_id;
      const m = srId ? map[srId] : undefined;
      if (!m) continue;
      m.order[s.step_key] = s.step_order;
      if (s.step_order > m.total) m.total = s.step_order;
    }
    return map;
  },

  /**
   * MC-C4 · complete a Master Card step-task: advances the task's linked units
   * (skip-conditional + card rollup) and marks the task done. The card stays
   * active until every unit finishes. Assignee or staff (tasks.edit).
   */
  async completeCardTask(taskId: string): Promise<{ task_id: string; status: string; units_advanced: number }> {
    if (API_URL) {
      try {
        return z
          .object({ task_id: z.string(), status: z.string(), units_advanced: z.coerce.number() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.completeCardTask(taskId), {
              method: "POST",
            }),
          ) as { task_id: string; status: string; units_advanced: number };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.completeCardTask", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("complete_service_request_task", {
      p_task_id: taskId,
    });
    if (error) throw error;
    return data as { task_id: string; status: string; units_advanced: number };
  },

  /**
   * MC-A6 · available units for a NEW request of `serviceType` on a property.
   * Returns every unit with is_available + unavailable_reason. Company/owner
   * access is enforced inside the SECURITY DEFINER RPC (returns [] if not
   * authorized), so no client-side company filter is needed.
   */
  async getAvailableUnits(propertyId: string, serviceType: string): Promise<AvailableUnit[]> {
    if (API_URL) {
      try {
        return z
          .array(
            z.object({
              unit_id: z.string(),
              unit_number: z.string().nullable(),
              is_available: z.boolean(),
              unavailable_reason: z.string().nullable(),
            }),
          )
          .parse(
            await nestRequest<unknown[]>(
              API_ROUTES.serviceRequests.availableUnits(propertyId, serviceType),
            ),
          ) as AvailableUnit[];
      } catch (err) {
        reportNestFallback("serviceRequests.getAvailableUnits", err);
      }
    }
    // ── Supabase fallback (local dev / Nest error) ──────────────────────────
    // RPC not yet in the generated types — cast the call (mirrors the from(VIEW as any) idiom).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("get_available_units", {
      p_property_id: propertyId,
      p_service_type: serviceType,
    });
    if (error) throw error;
    return (data ?? []) as AvailableUnit[];
  },

  /**
   * MC-A5 · create one Master Card (header + N units) via the transactional RPC.
   * Amounts are derived + persisted server-side; idempotent by idempotencyKey.
   */
  async createCard(params: CreateCardParams): Promise<CreateCardResult> {
    if (API_URL) {
      try {
        return z
          .object({
            service_request_id: z.string(),
            request_number: z.string().nullable(),
            total_fees: z.coerce.number().optional(),
            vat_amount: z.coerce.number().optional(),
            unit_count: z.coerce.number().optional(),
            idempotent: z.boolean(),
          })
          .parse(
            // ownerId is NOT sent — the guarded endpoint resolves the owner from the
            // JWT authoritatively (never trust a client-supplied owner id).
            await nestRequest<unknown>(API_ROUTES.serviceRequests.createCard(), {
              method: "POST",
              body: JSON.stringify({
                propertyId: params.propertyId,
                serviceType: params.serviceType,
                unitIds: params.unitIds,
                idempotencyKey: params.idempotencyKey,
                contractIntake: params.contractIntake,
                unitAmounts: params.unitAmounts,
              }),
            }),
          ) as CreateCardResult;
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.createCard", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("create_service_request", {
      p_owner_id: params.ownerId,
      p_property_id: params.propertyId,
      p_service_type: params.serviceType,
      p_unit_ids: params.unitIds,
      p_idempotency_key: params.idempotencyKey ?? null,
      p_contract_intake: params.contractIntake ?? null,
      p_unit_amounts: params.unitAmounts ?? null,
    });
    if (error) throw error;
    return data as CreateCardResult;
  },

  /**
   * MC-D1/D2 · employee submits a unit's inspection result (readiness + notes +
   * photos). Validates the readiness combo server-side; the step stays active
   * awaiting admin review (MC-D4). Assignee or staff.
   */
  async submitInspectionResult(params: {
    unitId: string;
    ready: boolean;
    needsMaintenance: boolean;
    needsCleaning: boolean;
    notes?: string;
    photoUrls?: string[];
  }): Promise<{ unit_id: string; status: string }> {
    if (API_URL) {
      try {
        return z
          .object({ unit_id: z.string(), status: z.string() })
          .parse(
            await nestRequest<unknown>(
              API_ROUTES.serviceRequests.submitInspectionResult(params.unitId),
              {
                method: "POST",
                body: JSON.stringify({
                  ready: params.ready,
                  needsMaintenance: params.needsMaintenance,
                  needsCleaning: params.needsCleaning,
                  notes: params.notes,
                  photoUrls: params.photoUrls,
                }),
              },
            ),
          ) as { unit_id: string; status: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.submitInspectionResult", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("submit_inspection_result", {
      p_unit_id: params.unitId,
      p_ready: params.ready,
      p_needs_maintenance: params.needsMaintenance,
      p_needs_cleaning: params.needsCleaning,
      p_notes: params.notes ?? null,
      p_photo_urls: params.photoUrls ?? null,
    });
    if (error) throw error;
    return data as { unit_id: string; status: string };
  },

  /**
   * MC-D3/D4 · admin reviews a submitted inspection. Approve → branches the unit
   * (creates maintenance/cleaning actions per readiness) and advances it; Return
   * → sends it back to the employee. Staff (service_requests.edit).
   */
  async reviewInspection(params: {
    unitId: string;
    approved: boolean;
    notes?: string;
  }): Promise<{ unit_id: string; result: string; actions_created?: number }> {
    if (API_URL) {
      try {
        return z
          .object({ unit_id: z.string(), result: z.string(), actions_created: z.coerce.number().optional() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.reviewInspection(params.unitId), {
              method: "POST",
              body: JSON.stringify({ approved: params.approved, notes: params.notes }),
            }),
          ) as { unit_id: string; result: string; actions_created?: number };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.reviewInspection", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("review_inspection", {
      p_unit_id: params.unitId,
      p_approved: params.approved,
      p_notes: params.notes ?? null,
    });
    if (error) throw error;
    return data as { unit_id: string; result: string; actions_created?: number };
  },

  /** Contracting · review the whole card's assessment at once (all units). */
  async reviewCardInspection(params: {
    cardId: string;
    approved: boolean;
    notes?: string;
  }): Promise<{ reviewed_units: number }> {
    if (API_URL) {
      try {
        return z
          .object({ service_request_id: z.string(), reviewed_units: z.number(), approved: z.boolean() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.reviewCardInspection(params.cardId), {
              method: "POST",
              body: JSON.stringify({ approved: params.approved, notes: params.notes }),
            }),
          ) as { service_request_id: string; reviewed_units: number; approved: boolean };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.reviewCardInspection", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("review_service_request_inspection", {
      p_service_request_id: params.cardId,
      p_approved: params.approved,
      p_notes: params.notes ?? null,
    });
    if (error) throw error;
    return data as { reviewed_units: number };
  },

  /** Visits · the assigned employee submits a unit's visit report. Nest-only. */
  async submitVisitResult(params: {
    unitId: string;
    notes?: string;
    photoUrls?: string[];
  }): Promise<{ unit_id: string; status: string }> {
    return z
      .object({ unit_id: z.string(), status: z.string() })
      .parse(
        await nestRequest<unknown>(API_ROUTES.serviceRequests.submitVisitResult(params.unitId), {
          method: "POST",
          body: JSON.stringify({ notes: params.notes, photoUrls: params.photoUrls }),
        }),
      ) as { unit_id: string; status: string };
  },

  /** Visits · admin reviews a submitted visit report (approve → contracting / return). */
  async reviewVisit(params: {
    unitId: string;
    approved: boolean;
    notes?: string;
  }): Promise<{ unit_id: string; result: string }> {
    return z
      .object({ unit_id: z.string(), result: z.string() })
      .parse(
        await nestRequest<unknown>(API_ROUTES.serviceRequests.reviewVisit(params.unitId), {
          method: "POST",
          body: JSON.stringify({ approved: params.approved, notes: params.notes }),
        }),
      ) as { unit_id: string; result: string };
  },

  /** Visits · units at the visits step for a task (employee report page). Nest-only. */
  async getTaskVisitSteps(taskId: string): Promise<Array<{ id: string; unitId: string; submitted: boolean; returned: boolean; reviewNotes: string | null }>> {
    return z
      .array(inspectionStepSchema)
      .parse(
        await nestRequest<unknown[]>(API_ROUTES.serviceRequests.taskVisitSteps(taskId)),
      ) as Array<{ id: string; unitId: string; submitted: boolean; returned: boolean; reviewNotes: string | null }>;
  },

  /** Contracting · the assigned employee submits a unit's draft contract. Nest-only. */
  /** Update the Master Card service price book (marketing + contracting fees).
   *  Admin-editable (server-gated on settings permission). */
  async updateServicePrices(p: { marketing: number; contracting: number }): Promise<void> {
    await nestRequest<unknown>(API_ROUTES.serviceRequests.servicePrices(), {
      method: "PUT",
      body: JSON.stringify(p),
    });
  },

  /** A unit's contract-draft PDF version history (signed), owner-or-staff scoped. */
  async getUnitContractDrafts(unitId: string): Promise<ContractDraftVersion[]> {
    return z
      .array(contractDraftVersionSchema)
      .parse(await nestRequest<unknown>(API_ROUTES.serviceRequests.unitContractDrafts(unitId)));
  },

  async submitContractDraft(params: {
    unitId: string;
    /** Bare `documents`-bucket path of the uploaded contract PDF. */
    pdfPath: string;
    employeeNote?: string;
  }): Promise<{ unit_id: string; version: number; status: string }> {
    const { unitId, ...body } = params;
    return z
      .object({ unit_id: z.string(), version: z.number(), status: z.string() })
      .parse(
        await nestRequest<unknown>(API_ROUTES.serviceRequests.submitContractDraft(unitId), {
          method: "POST",
          body: JSON.stringify(body),
        }),
      ) as { unit_id: string; version: number; status: string };
  },

  /** Contracting · the OWNER reviews the draft (approve → complete / return). */
  async ownerReviewContractDraft(params: {
    unitId: string;
    approved: boolean;
    notes?: string;
  }): Promise<{ unit_id: string; result: string }> {
    return z
      .object({ unit_id: z.string(), result: z.string() })
      .parse(
        await nestRequest<unknown>(API_ROUTES.serviceRequests.reviewContractDraft(params.unitId), {
          method: "POST",
          body: JSON.stringify({ approved: params.approved, notes: params.notes }),
        }),
      ) as { unit_id: string; result: string };
  },

  /** Contracting · the assigned employee activates the owner-approved draft into
   *  a live contract (links the just-created contract to the unit + completes it). */
  async activateContractFromDraft(params: {
    unitId: string;
    contractId: string;
  }): Promise<{ unit_id: string; contract_id: string; result: string }> {
    return z
      .object({ unit_id: z.string(), contract_id: z.string(), result: z.string() })
      .parse(
        await nestRequest<unknown>(API_ROUTES.serviceRequests.activateContractDraft(params.unitId), {
          method: "POST",
          body: JSON.stringify({ contractId: params.contractId }),
        }),
      ) as { unit_id: string; contract_id: string; result: string };
  },

  /** Contracting · units at the contracting step for a task (employee draft page). Nest-only. */
  async getTaskContractSteps(taskId: string): Promise<ContractStep[]> {
    return z
      .array(contractStepSchema)
      .parse(
        await nestRequest<unknown[]>(API_ROUTES.serviceRequests.taskContractSteps(taskId)),
      ) as ContractStep[];
  },

  /**
   * MC-C2 · assign a unit's active step (e.g. inspection) to an employee, from
   * the Master Card. Assigns the step's task (auto-created one or a new one) so
   * the employee can act in their portal. Staff (tasks.edit).
   */
  async assignUnitStep(params: { unitId: string; employeeId: string }): Promise<{ unit_id: string; task_id: string; step_key: string; assigned_to: string; property_assigned?: boolean }> {
    if (API_URL) {
      try {
        // `property_assigned` (B9) is true only when the assign newly granted the
        // employee the card's property — keep it so the UI can notify the admin.
        return z
          .object({ unit_id: z.string(), task_id: z.string(), step_key: z.string(), assigned_to: z.string(), property_assigned: z.boolean().optional() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.assignUnitStep(params.unitId), {
              method: "POST",
              body: JSON.stringify({ employeeId: params.employeeId }),
            }),
          ) as { unit_id: string; task_id: string; step_key: string; assigned_to: string; property_assigned?: boolean };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.assignUnitStep", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("assign_unit_step", {
      p_unit_id: params.unitId,
      p_employee_id: params.employeeId,
    });
    if (error) throw error;
    return data as { unit_id: string; task_id: string; step_key: string; assigned_to: string; property_assigned?: boolean };
  },

  /** Contracting · assign the WHOLE card (every unit's active step) to one
   *  employee. One contract for all units → one employee for all units. */
  async assignCardUnits(params: { cardId: string; employeeId: string }): Promise<{ assigned_units: number; property_assigned?: boolean }> {
    if (API_URL) {
      try {
        return z
          .object({ service_request_id: z.string(), assigned_units: z.number(), employee_id: z.string(), property_assigned: z.boolean().optional() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.assignCardUnits(params.cardId), {
              method: "POST",
              body: JSON.stringify({ employeeId: params.employeeId }),
            }),
          ) as { service_request_id: string; assigned_units: number; employee_id: string; property_assigned?: boolean };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.assignCardUnits", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("assign_service_request_units", {
      p_service_request_id: params.cardId,
      p_employee_id: params.employeeId,
    });
    if (error) throw error;
    return data as { assigned_units: number; property_assigned?: boolean };
  },

  /**
   * MC-C2 · login-linked, active employees of a SPECIFIC company — for the card's
   * assign picker (must offer the CARD's company, not the admin's active company).
   */
  async getCompanyEmployees(companyId: string): Promise<Array<{ id: string; full_name: string | null }>> {
    if (API_URL) {
      try {
        return z.array(assignableEmployeeSchema).parse(
          await nestRequest<unknown[]>(
            API_ROUTES.serviceRequests.assignableEmployees(companyId),
          ),
        ) as Array<{ id: string; full_name: string | null }>;
      } catch (err) {
        reportNestFallback("serviceRequests.getCompanyEmployees", err);
      }
    }
    // ── Supabase fallback (local dev / Nest error) ──────────────────────────
    // The `employees` table RLS only lets SUPER admins SELECT, so a regular admin's
    // direct read comes back empty. get_assignable_employees is a SECURITY DEFINER
    // reader that does its own staff check and returns the login-linked, active
    // members of the company — the assignable set for the card step. (mig 147000)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("get_assignable_employees", {
      p_company_id: companyId,
    });
    if (error) throw error;
    return (data ?? []) as Array<{ id: string; full_name: string | null }>;
  },

  /** MC-I · service providers of a SPECIFIC company — for the card-scoped assign picker. */
  async getCompanyProviders(companyId: string): Promise<Array<{ id: string; name: string | null }>> {
    if (API_URL) {
      try {
        return z.array(assignableProviderSchema).parse(
          await nestRequest<unknown[]>(
            API_ROUTES.serviceRequests.assignableProviders(companyId),
          ),
        ) as Array<{ id: string; name: string | null }>;
      } catch (err) {
        reportNestFallback("serviceRequests.getCompanyProviders", err);
      }
    }
    // ── Supabase fallback (local dev / Nest error) ──────────────────────────
    // Same RLS posture as employees — use the SECURITY DEFINER reader so a regular
    // admin can populate the "Assign provider" picker. (mig 147000)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("get_assignable_providers", {
      p_company_id: companyId,
    });
    if (error) throw error;
    return (data ?? []) as Array<{ id: string; name: string | null }>;
  },

  /**
   * MC-D1 · the per-unit inspection steps an employee must fill for a task. Reads
   * service_request_steps directly — the assigned employee sees their own steps via
   * RLS (assigned_employee_id) once the admin assigns the task (propagated by trigger).
   * Returns the service_request_unit id to inspect + whether it's already submitted.
   */
  async getTaskInspectionSteps(taskId: string): Promise<Array<{ id: string; unitId: string; unitNumber: string | null; notes: string | null; photos: string[]; submitted: boolean; returned: boolean; reviewNotes: string | null; serviceType?: string }>> {
    if (API_URL) {
      try {
        return z.array(inspectionStepSchema).parse(
          await nestRequest<unknown[]>(
            API_ROUTES.serviceRequests.taskInspectionSteps(taskId),
          ),
        ) as Array<{ id: string; unitId: string; unitNumber: string | null; notes: string | null; photos: string[]; submitted: boolean; returned: boolean; reviewNotes: string | null; serviceType?: string }>;
      } catch (err) {
        reportNestFallback("serviceRequests.getTaskInspectionSteps", err);
      }
    }
    // ── Supabase fallback (local dev / Nest error) ──────────────────────────
    // service_request_steps isn't in the generated types — cast the source.
    // Embed unit → service_request to surface service_type (report-only gating).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.from as any)("service_request_steps")
      .select("id, service_request_unit_id, step_status, metadata, service_request_units(service_requests(service_type))")
      .eq("task_id", taskId)
      .eq("step_key", "inspection");
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data ?? []) as any[]).map((s) => {
      const m = (s.metadata as { status?: string; review_notes?: string; notes?: string; photos?: string[] } | null) ?? null;
      // PostgREST returns to-one embeds as an object (or array if ambiguous).
      const unit = Array.isArray(s.service_request_units) ? s.service_request_units[0] : s.service_request_units;
      const sr = Array.isArray(unit?.service_requests) ? unit?.service_requests[0] : unit?.service_requests;
      return {
        id: s.id as string,
        unitId: s.service_request_unit_id as string,
        // The degraded fallback doesn't resolve the unit number or signed photo
        // docs (Nest is the primary path); metadata notes are best-effort.
        unitNumber: null as string | null,
        notes: m?.notes ?? null,
        photos: [] as string[],
        submitted: m?.status === "submitted" || m?.status === "approved",
        returned: m?.status === "returned",
        reviewNotes: m?.review_notes ?? null,
        serviceType: (sr?.service_type as string | undefined) ?? undefined,
      };
    });
  },

  /** The maintenance/cleaning actions the assigned employee completes for a task. Nest-only. */
  async getTaskActionCompletions(taskId: string): Promise<TaskActionCompletion[]> {
    return z
      .array(taskActionCompletionSchema)
      .parse(
        await nestRequest<unknown[]>(API_ROUTES.serviceRequests.taskActionCompletions(taskId)),
      ) as TaskActionCompletion[];
  },

  /**
   * MC-E1 · assigned provider/staff submits a quote (amount + optional quote doc)
   * for a maintenance action. Moves the action pending → quoted, awaiting admin
   * review (MC-E2). VAT is derived server-side. Assignee or staff.
   */
  async submitActionQuote(params: {
    actionId: string;
    amount: number;
    quoteUrl?: string;
    // F3 · quote composer extras
    quoteNote?: string;
    includeAssessment?: boolean;
    includeMaintenanceReport?: boolean;
    attachmentDocIds?: string[];
  }): Promise<{ action_id: string; status: string }> {
    if (API_URL) {
      try {
        return z
          .object({ action_id: z.string(), status: z.string() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.submitActionQuote(params.actionId), {
              method: "POST",
              body: JSON.stringify({
                amount: params.amount,
                quoteUrl: params.quoteUrl,
                quoteNote: params.quoteNote,
                includeAssessment: params.includeAssessment,
                includeMaintenanceReport: params.includeMaintenanceReport,
                attachmentDocIds: params.attachmentDocIds,
              }),
            }),
          ) as { action_id: string; status: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.submitActionQuote", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("submit_action_quote", {
      p_action_id: params.actionId,
      p_amount: params.amount,
      p_quote_url: params.quoteUrl ?? null,
    });
    if (error) throw error;
    return data as { action_id: string; status: string };
  },

  /**
   * MC-E2 · admin reviews a submitted quote. Approve → forwards the action to the
   * owner for a decision (MC-E3); Return → sends it back for re-quoting. Staff
   * (service_requests.edit).
   */
  async reviewActionQuote(params: {
    actionId: string;
    approved: boolean;
    notes?: string;
  }): Promise<{ action_id: string; status: string }> {
    if (API_URL) {
      try {
        return z
          .object({ action_id: z.string(), status: z.string() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.reviewActionQuote(params.actionId), {
              method: "POST",
              body: JSON.stringify({ approved: params.approved, notes: params.notes }),
            }),
          ) as { action_id: string; status: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.reviewActionQuote", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("review_action_quote", {
      p_action_id: params.actionId,
      p_approved: params.approved,
      p_notes: params.notes ?? null,
    });
    if (error) throw error;
    return data as { action_id: string; status: string };
  },

  /**
   * MC-E3 · owner accepts or rejects a quoted maintenance action. Accept → moves
   * it to in_progress; Reject (with reason) → routes it to admin resolution
   * (MC-E4). Type-agnostic RPC reused for cleaning too. Owner portal.
   */
  async ownerDecideAction(params: {
    actionId: string;
    approved: boolean;
    reason?: string;
  }): Promise<{ action_id: string; status: string }> {
    if (API_URL) {
      try {
        return z
          .object({ action_id: z.string(), status: z.string() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.ownerDecideAction(params.actionId), {
              method: "POST",
              body: JSON.stringify({ approved: params.approved, reason: params.reason }),
            }),
          ) as { action_id: string; status: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.ownerDecideAction", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("owner_decide_action", {
      p_action_id: params.actionId,
      p_approved: params.approved,
      p_reason: params.reason ?? null,
    });
    if (error) throw error;
    return data as { action_id: string; status: string };
  },

  /**
   * Pkg 3 · senior staff starts work WITHOUT an online charge (offline/bank
   * payment, absorbed cost). Permission-gated to staff (never the owner),
   * requires a written reason, and is audited — see override_action_payment.
   */
  async overrideActionPayment(params: {
    actionId: string;
    reason: string;
  }): Promise<{ action_id: string; status: string; overridden: boolean }> {
    if (API_URL) {
      try {
        return z
          .object({ action_id: z.string(), status: z.string(), overridden: z.boolean() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.overrideActionPayment(params.actionId), {
              method: "POST",
              body: JSON.stringify({ reason: params.reason }),
            }),
          ) as { action_id: string; status: string; overridden: boolean };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.overrideActionPayment", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("override_action_payment", {
      p_action_id: params.actionId,
      p_reason: params.reason,
    });
    if (error) throw error;
    return data as { action_id: string; status: string; overridden: boolean };
  },

  /**
   * MC-E4 · admin resolves an owner-rejected action — renegotiate (re-quote),
   * reassign to a different provider, or cancel the action. `choice` drives the
   * branch; `providerId` is required when reassigning. Staff (service_requests.edit).
   */
  async adminResolveRejection(params: {
    actionId: string;
    choice: string;
    providerId?: string;
  }): Promise<{ action_id: string; status: string }> {
    if (API_URL) {
      try {
        return z
          .object({ action_id: z.string(), status: z.string() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.adminResolveRejection(params.actionId), {
              method: "POST",
              body: JSON.stringify({ choice: params.choice, providerId: params.providerId }),
            }),
          ) as { action_id: string; status: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.adminResolveRejection", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("admin_resolve_rejection", {
      p_action_id: params.actionId,
      p_choice: params.choice,
      p_provider_id: params.providerId ?? null,
    });
    if (error) throw error;
    return data as { action_id: string; status: string };
  },

  /**
   * MC-E5 · provider/staff submits the completion report for an in-progress
   * action. Moves it to completed_pending_admin, awaiting admin sign-off (MC-E6).
   * Type-agnostic RPC reused for cleaning too. Assignee or staff.
   */
  async submitActionCompletion(params: {
    actionId: string;
    reportUrl?: string;
    completionNote?: string;
    /** Sequential flow · flag another need discovered during this job. */
    needsFollowup?: "maintenance" | "cleaning" | null;
  }): Promise<{ action_id: string; status: string }> {
    if (API_URL) {
      try {
        return z
          .object({ action_id: z.string(), status: z.string() })
          .parse(
            await nestRequest<unknown>(
              API_ROUTES.serviceRequests.submitActionCompletion(params.actionId),
              {
                method: "POST",
                body: JSON.stringify({
                  reportUrl: params.reportUrl,
                  completionNote: params.completionNote,
                  needsFollowup: params.needsFollowup ?? undefined,
                }),
              },
            ),
          ) as { action_id: string; status: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.submitActionCompletion", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("submit_action_completion", {
      p_action_id: params.actionId,
      p_report_url: params.reportUrl ?? "(completion report attached)",
      p_needs_followup: params.needsFollowup ?? null,
    });
    if (error) throw error;
    return data as { action_id: string; status: string };
  },

  /**
   * MC-E6 · admin approves an action's completion report. Moves it to completed
   * and lets the unit's pipeline advance. Type-agnostic RPC reused for cleaning
   * too. Staff (service_requests.edit).
   */
  async approveActionCompletion(params: {
    actionId: string;
  }): Promise<{ action_id: string; status: string }> {
    if (API_URL) {
      try {
        return z
          .object({ action_id: z.string(), status: z.string() })
          .parse(
            await nestRequest<unknown>(
              API_ROUTES.serviceRequests.approveActionCompletion(params.actionId),
              { method: "POST" },
            ),
          ) as { action_id: string; status: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.approveActionCompletion", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("approve_action_completion", {
      p_action_id: params.actionId,
    });
    if (error) throw error;
    return data as { action_id: string; status: string };
  },

  /** MC-F2 · admin overrides / sets a cleaning action's fixed price. */
  async setCleaningPrice(params: { actionId: string; amount: number }): Promise<{ action_id: string; status: string }> {
    if (API_URL) {
      try {
        return z
          .object({ action_id: z.string(), status: z.string() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.setCleaningPrice(params.actionId), {
              method: "POST",
              body: JSON.stringify({ amount: params.amount }),
            }),
          ) as { action_id: string; status: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.setCleaningPrice", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("set_cleaning_price", {
      p_action_id: params.actionId,
      p_amount: params.amount,
    });
    if (error) throw error;
    return data as { action_id: string; status: string };
  },

  /** MC-H1 · employee reports an ad-hoc note (optionally tied to a unit + photo). */
  async reportNote(params: {
    serviceRequestId: string;
    note: string;
    unitId?: string;
    photoUrl?: string;
  }): Promise<{ note_id: string }> {
    if (API_URL) {
      try {
        return z
          .object({ note_id: z.string() })
          .parse(
            await nestRequest<unknown>(
              API_ROUTES.serviceRequests.reportNote(params.serviceRequestId),
              {
                method: "POST",
                body: JSON.stringify({
                  note: params.note,
                  unitId: params.unitId,
                  photoUrl: params.photoUrl,
                }),
              },
            ),
          ) as { note_id: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.reportNote", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("report_service_request_note", {
      p_service_request_id: params.serviceRequestId,
      p_note: params.note,
      p_unit_id: params.unitId ?? null,
      p_photo_url: params.photoUrl ?? null,
    });
    if (error) throw error;
    return data as { note_id: string };
  },

  /** MC-H2 · admin converts a note into a tracked maintenance/cleaning action. */
  async convertNoteToAction(params: { noteId: string; actionType: ActionType }): Promise<{ note_id: string; action_id: string }> {
    if (API_URL) {
      try {
        return z
          .object({ note_id: z.string(), action_id: z.string() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.convertNoteToAction(params.noteId), {
              method: "POST",
              body: JSON.stringify({ actionType: params.actionType }),
            }),
          ) as { note_id: string; action_id: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.convertNoteToAction", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("convert_note_to_action", {
      p_note_id: params.noteId,
      p_action_type: params.actionType,
    });
    if (error) throw error;
    return data as { note_id: string; action_id: string };
  },

  /** MC-H2 · admin dismisses a note. */
  async dismissNote(params: { noteId: string }): Promise<{ note_id: string }> {
    if (API_URL) {
      try {
        return z
          .object({ note_id: z.string() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.dismissNote(params.noteId), {
              method: "POST",
            }),
          ) as { note_id: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.dismissNote", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("dismiss_service_request_note", {
      p_note_id: params.noteId,
    });
    if (error) throw error;
    return data as { note_id: string };
  },

  /**
   * MC-I1 · the current provider's OPEN assigned actions with minimal context
   * (task-scoped — no owner identity / full card). Drives the provider portal.
   */
  async getProviderActions(): Promise<ProviderAction[]> {
    if (API_URL) {
      try {
        return z
          .array(
            z.object({
              id: z.string(),
              action_type: z.string(),
              status: z.string(),
              amount: z.coerce.number().nullable(),
              vat_amount: z.coerce.number().nullable(),
              quote_document_url: z.string().nullable(),
              completion_report_url: z.string().nullable(),
              unit_number: z.string().nullable(),
              property_name: z.string().nullable(),
              service_type: z.string(),
              request_number: z.string().nullable(),
              created_at: z.string(),
            }),
          )
          .parse(
            await nestRequest<unknown[]>(API_ROUTES.serviceRequests.providerActions()),
          ) as ProviderAction[];
      } catch (err) {
        reportNestFallback("serviceRequests.getProviderActions", err);
      }
    }
    // ── Supabase fallback (local dev / Nest error) ──────────────────────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("get_provider_actions");
    if (error) throw error;
    return (data ?? []) as ProviderAction[];
  },

  /**
   * F1 · list a unit's uploaded documents (each with a fresh signed read URL).
   * Nest-only — the per-unit documents table is served exclusively by the guarded
   * endpoint (there is no Supabase fallback path for this new resource).
   */
  async listUnitDocuments(unitId: string): Promise<UnitDocument[]> {
    return z
      .array(unitDocumentSchema)
      .parse(
        await nestRequest<unknown[]>(API_ROUTES.serviceRequests.unitDocuments(unitId)),
      ) as UnitDocument[];
  },

  /** F1 · every unit's documents for a whole card (admin aggregate). Nest-only. */
  async listCardDocuments(id: string): Promise<CardDocument[]> {
    return z
      .array(cardDocumentSchema)
      .parse(
        await nestRequest<unknown[]>(API_ROUTES.serviceRequests.cardDocuments(id)),
      ) as CardDocument[];
  },

  /** The real contract registered for a contracting card (null until one is). */
  async getCardContract(id: string): Promise<{ id: string; contract_number: string | null } | null> {
    const raw = await nestRequest<unknown>(API_ROUTES.serviceRequests.cardContract(id));
    if (!raw) return null;
    return z
      .object({ id: z.string(), contract_number: z.string().nullable().default(null) })
      .parse(raw) as { id: string; contract_number: string | null };
  },

  /** Owner "Pay all": bundle the card's still-due service-fee invoices into ONE
   *  master invoice (or a single invoice when only one is due). */
  async payCardServiceFees(cardId: string): Promise<{ masterId?: string; invoiceId?: string; dueCount: number }> {
    return z
      .object({ masterId: z.string().optional(), invoiceId: z.string().optional(), dueCount: z.number() })
      .parse(
        await nestRequest<unknown>(API_ROUTES.serviceRequests.payServiceFees(cardId), { method: "POST" }),
      ) as { masterId?: string; invoiceId?: string; dueCount: number };
  },

  /** The real contract registered for ONE unit (marketing, per-unit; null until). */
  async getUnitContract(unitId: string): Promise<{ id: string; contract_number: string | null } | null> {
    const raw = await nestRequest<unknown>(API_ROUTES.serviceRequests.unitContract(unitId));
    if (!raw) return null;
    return z
      .object({ id: z.string(), contract_number: z.string().nullable().default(null) })
      .parse(raw) as { id: string; contract_number: string | null };
  },

  /**
   * F1 · record a per-unit document the client already uploaded to the private
   * `documents` bucket. Returns the new row id. Nest-only.
   */
  async addUnitDocument(
    unitId: string,
    params: { filePath: string; docType?: string; caption?: string },
  ): Promise<{ id: string }> {
    return z
      .object({ id: z.string() })
      .parse(
        await nestRequest<unknown>(API_ROUTES.serviceRequests.unitDocuments(unitId), {
          method: "POST",
          body: JSON.stringify({
            filePath: params.filePath,
            docType: params.docType,
            caption: params.caption,
          }),
        }),
      ) as { id: string };
  },

  /**
   * F5 · admin returns a Master Card to the owner with a reason. The owner then
   * sees the reason and can add/remove units. Nest-only (guarded endpoint).
   */
  async returnCardToOwner(id: string, reason: string): Promise<{ returned: boolean }> {
    return z
      .object({ returned: z.boolean() })
      .parse(
        await nestRequest<unknown>(API_ROUTES.serviceRequests.returnToOwner(id), {
          method: "POST",
          body: JSON.stringify({ reason }),
        }),
      ) as { returned: boolean };
  },

  /** F5 · owner (or staff) adds available units to the card. Nest-only. */
  async ownerAddUnits(id: string, unitIds: string[]): Promise<{ added: number }> {
    return z
      .object({ added: z.number() })
      .parse(
        await nestRequest<unknown>(API_ROUTES.serviceRequests.ownerAddUnits(id), {
          method: "POST",
          body: JSON.stringify({ unitIds }),
        }),
      ) as { added: number };
  },

  /**
   * F5 · owner (or staff) removes a NOT-started, un-charged unit from the card.
   * The server rejects a unit that has started work or carries charges. Nest-only.
   */
  async ownerRemoveUnit(id: string, unitId: string): Promise<{ removed: boolean }> {
    return z
      .object({ removed: z.boolean() })
      .parse(
        await nestRequest<unknown>(API_ROUTES.serviceRequests.ownerRemoveUnit(id, unitId), {
          method: "DELETE",
        }),
      ) as { removed: boolean };
  },

  /** F3 · the composer payload the owner/admin see with a quote. Nest-only. */
  async getActionQuoteDetails(actionId: string): Promise<ActionQuoteDetails> {
    return actionQuoteDetailsSchema.parse(
      await nestRequest<unknown>(API_ROUTES.serviceRequests.actionQuoteDetails(actionId)),
    ) as ActionQuoteDetails;
  },

  /** MC-I · admin assigns a provider to a maintenance/cleaning action. */
  async assignActionProvider(params: { actionId: string; providerId: string }): Promise<{ action_id: string; provider_id: string }> {
    if (API_URL) {
      try {
        return z
          .object({ action_id: z.string(), provider_id: z.string() })
          .parse(
            await nestRequest<unknown>(API_ROUTES.serviceRequests.assignActionProvider(params.actionId), {
              method: "POST",
              body: JSON.stringify({ providerId: params.providerId }),
            }),
          ) as { action_id: string; provider_id: string };
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceRequests.assignActionProvider", err);
      }
    }
    // ── Supabase fallback (local dev / safe pre-commit Nest error) ───────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("assign_action_provider", {
      p_action_id: params.actionId,
      p_provider_id: params.providerId,
    });
    if (error) throw error;
    return data as { action_id: string; provider_id: string };
  },

  /**
   * Update a request. company-scoped unless scopeToCompany is false.
   * When `returning` is true, the updated row is selected and returned;
   * otherwise it is a fire-and-forget update.
   *
   * Flipped to Nest (PUT) for ALL roles — the guarded endpoint resolves scope
   * from the JWT (super_admin writes cross-company there). Supabase remains the
   * fallback (still honouring scopeToCompany). `inspection_report`/`properties_json`
   * are Json-typed in the Nest UpdateDto, so object-valued updates validate and
   * reach Nest.
   */
  async update(
    id: string,
    patch: TablesUpdate<"service_requests">,
    opts: ScopeOptions & { returning?: boolean },
  ): Promise<ServiceRequest | null> {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.serviceRequests.update(id), {
          method: "PUT",
          body: JSON.stringify(patch),
        });
        if (!opts.returning) return null;
        return row ? serviceRequestSchema.parse(row) : null;
      } catch (err) {
        reportNestFallback("serviceRequests.update", err);
      }
    }
    return updateViaSupabase(id, patch, opts);
  },

  /**
   * Status counts for the company (or ALL companies for super-admin).
   * Flipped to Nest first; the guarded `/service-requests/stats` resolves scope
   * from the JWT (super_admin reads cross-company, mirroring scopeToCompany:false).
   * Supabase is the fallback on any Nest error and the live path in local dev
   * (VITE_API_URL unset) — structurally identical to `list()`. RLS untouched.
   */
  async stats({
    companyId,
    scopeToCompany = true,
    selectedCompanyId,
    startDate,
    endDate,
  }: ScopeOptions): Promise<ServiceRequestStats> {
    if (API_URL) {
      try {
        const data = await nestRequest<unknown>(
          API_ROUTES.serviceRequests.stats(selectedCompanyId ?? undefined, startDate, endDate),
        );
        return serviceRequestStatsSchema.parse(data);
      } catch (err) {
        reportNestFallback("serviceRequests.stats", err);
      }
    }

    let query = supabase.from(TABLE).select("id, status");
    if (scopeToCompany) query = query.eq("company_id", companyId as string);
    else if (selectedCompanyId) query = query.eq("company_id", selectedCompanyId);
    if (startDate) query = query.gte("created_at", startDate);
    if (endDate) query = query.lte("created_at", endDate);

    const { data, error } = await query;
    if (error) throw error;

    return {
      total: data.length,
      new: data.filter((r) => r.status === "new").length,
      under_review: data.filter((r) => r.status === "under_review").length,
      under_inspection: data.filter((r) => r.status === "under_inspection").length,
      approved: data.filter((r) => r.status === "approved").length,
      rejected: data.filter((r) => r.status === "rejected").length,
    };
  },

  /**
   * Companies that have requests — the super_admin dashboard's company tab strip
   * (super_admin → all companies with requests + the main company always; any
   * other role → just their own company). Flipped to Nest first; the guarded
   * `/service-requests/companies` resolves scope from the JWT. Supabase is the
   * fallback on any Nest error and the live path in local dev (VITE_API_URL unset).
   */
  async companiesWithRequests({ companyId, scopeToCompany = true }: ScopeOptions): Promise<ServiceRequestCompany[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown>(API_ROUTES.serviceRequests.companies());
        return z.array(serviceRequestCompanySchema).parse(rows);
      } catch (err) {
        reportNestFallback("serviceRequests.companies", err);
      }
    }
    return companiesWithRequestsViaSupabase({ companyId, scopeToCompany });
  },
};
